import React, { useRef } from 'react';
import { Box } from '@mui/material';
import { DraggableComponentProps } from 'features/document/constants';
import { isQuoteAcceptanceInfoTableElement } from 'utils/typeGuards';
import '../Table/Table.css';
import { useAppDispatch } from 'app/hooks';
import { updateContentElementById } from 'features/document/documentSlice';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import './QuoteAcceptanceInfoTable.css';

export default function QuoteAcceptanceInfoTable({ currentElement }: DraggableComponentProps) {
  if (!isQuoteAcceptanceInfoTableElement(currentElement)) {
    return null;
  }

  const dispatch = useAppDispatch();
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const { content: elementContent } = currentElement;
  const {
    table,
    acceptanceInfo: { signature, date },
  } = elementContent;

  const { columns, rows, cells } = table;

  const getSidebarByCellId = (cellId: string) => {
    if (cellId.includes('signature')) {
      return 'signature';
    }
    if (cellId.includes('date')) {
      return 'date';
    }
    return 'acceptanceInfo';
  };

  const getTextAlignByCellId = (cellId: string) => {
    if (cellId.includes('signature')) {
      return signature.justifyContent;
    }
    if (cellId.includes('date')) {
      return date.justifyContent;
    }
    return 'center';
  };

  const handleEditTitleCell = (content: string) => {
    const newContent = {
      ...elementContent,
      acceptanceInfo: {
        ...elementContent.acceptanceInfo,
        title: content,
      },
      table: {
        ...elementContent.table,
        cells: {
          ...elementContent.table.cells,
          'cell-title': {
            ...elementContent.table.cells['cell-title'],
            content: content,
          },
        },
      },
    };

    dispatch(
      updateContentElementById({
        content: newContent,
      })
    );
  };

  const renderTitleCellContent = (content: string) => {
    return (
      <Editor
        apiKey='vso0zqjwhii0o3j8jdkhmhj882sgod8nd4bkbafrg32e1sak'
        onInit={(evt, editor) => (editorRef.current = editor)}
        init={{
          plugins: 'paste',
          toolbar:
            'undo redo fontsize  | blocks | ' +
            'bold italic forecolor | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'removeformat',
          inline: true,
          menubar: false,
          max_width: 300,
          fixed_toolbar_container: '#myToolbarContainer',
          content_css: './QuoteAcceptanceInfoTable.css',
        }}
        value={content}
        onEditorChange={(newValue, _editor) => handleEditTitleCell(newValue)}
      />
    );
  };

  const handleOnClickCell = (activeSidebar: string) => {
    dispatch(
      updateContentElementById({
        content: {
          ...elementContent,
          activeSidebar: activeSidebar,
        },
      })
    );
  };

  const renderTableHeader = () => {
    return (
      <thead>
        <tr>
          {columns.map((column, columnIndex) => (
            <th
              className='pricingHeader'
              colSpan={columnIndex === 0 ? 2 : 1}
              key={column.id}
              style={{ display: columnIndex === 1 ? 'none' : 'table-cell' }}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const renderTableBody = () => {
    return (
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr className={`row`} key={row.id}>
            {row.cellIds.map((cellId, cellIndex) => {
              const cell = cells[cellId];
              const isTitleCell = rowIndex === 0 && cellIndex === 1;
              return (
                <td
                  className={`${cellIndex === 0 ? 'labelCell' : 'cell'} ${getTextAlignByCellId(cell.id)}`}
                  key={cell.id}
                  onClick={() => handleOnClickCell(getSidebarByCellId(cell.id))}
                  style={{ padding: isTitleCell ? '0' : '8px 8px 6px 8px' }}
                  aria-hidden="true"
                >
                  {isTitleCell ? renderTitleCellContent(cell.content) : cell.content}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    );
  };

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'flex-end'} gap={'24px'}>
      <table className='table'>
        {renderTableHeader()}
        {renderTableBody()}
      </table>
    </Box>
  );
}
